import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getParticipant, getStartups, createInvestment, getParticipantInvestments } from '../services/api';
import InvestmentModal from '../components/InvestmentModal';
import StartupCard from '../components/StartupCard';

function JuryDashboard() {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token');
  const [token, setToken] = useState(urlToken || localStorage.getItem('jury_token') || '');
  const [participant, setParticipant] = useState(null);
  const [startups, setStartups] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [selectedStartup, setSelectedStartup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [draftInvestments, setDraftInvestments] = useState(() => {
    // Load draft investments from localStorage on initial render
    const savedDrafts = localStorage.getItem(`draft_investments_${urlToken || localStorage.getItem('jury_token')}`);
    return savedDrafts ? JSON.parse(savedDrafts) : [];
  });

  // Save draft investments to localStorage whenever they change
  useEffect(() => {
    if (token) {
      localStorage.setItem(`draft_investments_${token}`, JSON.stringify(draftInvestments));
    }
  }, [draftInvestments, token]);

  useEffect(() => {
    // If token comes from URL, save it to localStorage
    if (urlToken && urlToken !== token) {
      setToken(urlToken);
      localStorage.setItem('jury_token', urlToken);
      // Load draft investments for new token
      const savedDrafts = localStorage.getItem(`draft_investments_${urlToken}`);
      if (savedDrafts) {
        setDraftInvestments(JSON.parse(savedDrafts));
      } else {
        setDraftInvestments([]);
      }
    }
  }, [urlToken]);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [participantRes, startupsRes, investmentsRes] = await Promise.all([
        getParticipant(token),
        getStartups(),
        getParticipantInvestments(token)
      ]);
      
      if (participantRes.data.type !== 'jury') {
        setError('This token is not valid for the jury');
        setToken('');
        localStorage.removeItem('jury_token');
        return;
      }

      setParticipant(participantRes.data);
      setStartups(startupsRes.data);
      setInvestments(investmentsRes.data);
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
      setError(`Error loading data: ${errorMessage}. Please check your token and make sure the server is running.`);
      console.error('Full error:', err);
      console.error('Error response:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleTokenSubmit = (e) => {
    e.preventDefault();
    if (token) {
      localStorage.setItem('jury_token', token);
      loadData();
    }
  };

  const handleInvest = (startup) => {
    setSelectedStartup(startup);
  };

  const handleInvestmentComplete = (amount) => {
    const investmentAmount = parseFloat(amount);
    
    // If amount is 0 or empty, remove draft for this startup
    if (!amount || isNaN(investmentAmount) || investmentAmount <= 0) {
      setDraftInvestments(prev => prev.filter(draft => draft.startupId !== selectedStartup.id));
      setSelectedStartup(null);
      return;
    }

    // Validate that the total of all drafts (including this one) doesn't exceed budget
    if (!participant) return;
    
    const otherDraftsTotal = draftInvestments
      .filter(draft => draft.startupId !== selectedStartup.id)
      .reduce((sum, draft) => sum + draft.amount, 0);
    const totalWithNewInvestment = otherDraftsTotal + investmentAmount;
    const participantRemainingBudget = participant.remaining_budget;
    
    // Calculate available budget considering submitted investments
    const currentAvailableBudget = participantRemainingBudget - otherDraftsTotal;
    
    if (totalWithNewInvestment > participantRemainingBudget) {
      setError(`Das Gesamtbudget von ${participantRemainingBudget.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} würde überschritten. Verfügbar: ${currentAvailableBudget.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`);
      setSelectedStartup(null);
      return;
    }

    // Save investment as draft (locally, not sent to server yet)
    setDraftInvestments(prev => {
      // Remove existing draft for this startup if any
      const filtered = prev.filter(draft => draft.startupId !== selectedStartup.id);
      // Add new draft
      return [...filtered, { startupId: selectedStartup.id, startupName: selectedStartup.name, amount: investmentAmount }];
    });
    setSelectedStartup(null);
  };

  const handleSubmitAll = async () => {
    if (draftInvestments.length === 0) {
      setError('No investments to submit');
      return;
    }

    if (!participant) return;

    // Validate total doesn't exceed budget BEFORE submitting anything
    const totalDraftAmount = draftInvestments.reduce((sum, draft) => sum + draft.amount, 0);
    const participantRemainingBudget = participant.remaining_budget;
    if (totalDraftAmount > participantRemainingBudget) {
      setError(`Das Gesamtbudget von ${participantRemainingBudget.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} würde überschritten. Bitte passen Sie Ihre Investments an.`);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Submit all draft investments to server
      // If any fails, stop and show error (don't continue submitting)
      for (const draft of draftInvestments) {
        try {
          await createInvestment(token, draft.startupId, draft.amount);
        } catch (err) {
          // If one investment fails, reload data to get current state and show error
          await loadData();
          const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
          setError(`Fehler beim Absenden: ${errorMessage}. Bitte überprüfen Sie Ihre Investments.`);
          throw err; // Stop submitting further investments
        }
      }
      
      // Clear draft investments after successful submission
      setDraftInvestments([]);
      localStorage.removeItem(`draft_investments_${token}`);
      
      // Reload data to get updated investments and budget
      await loadData();
    } catch (err) {
      // Error already handled in the loop, just ensure loading is false
      // If error wasn't set in inner catch, set a generic error
      if (!err.response) {
        setError('Error submitting investments. Please try again.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // If token comes from URL (QR code) - show loading or error
  if (urlToken && !participant) {
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-primary-50 to-primary-100">
          <div className="card max-w-md w-full text-center">
            <div className="text-xl text-gray-600">Loading dashboard...</div>
          </div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-primary-50 to-primary-100">
          <div className="card max-w-md w-full">
            <div className="text-center mb-6">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Jury Dashboard</h1>
            </div>
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              {error}
            </div>
          </div>
        </div>
      );
    }
    // Still loading but not in loading state - show loading screen
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="card max-w-md w-full text-center">
          <div className="text-xl text-gray-600">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  // Only show token input if no URL token and no participant
  if (!urlToken && (!token || !participant)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="card max-w-md w-full">
          <div className="text-center mb-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Jury Dashboard</h1>
            <p className="text-gray-600">Please enter your token</p>
          </div>
          
          <form onSubmit={handleTokenSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Your Jury Token"
                className="input-field"
                required
              />
            </div>
            <button type="submit" className="btn-primary w-full">
              Login
            </button>
          </form>
          
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Only render dashboard if participant is loaded
  if (!participant) {
    return null;
  }

  const totalInvested = investments.reduce((sum, inv) => sum + parseFloat(inv.amount), 0);
  const remainingBudget = participant.remaining_budget;
  
  // Calculate total of draft investments to update available budget display
  const totalDraftAmount = draftInvestments.reduce((sum, draft) => sum + draft.amount, 0);
  const availableBudget = remainingBudget - totalDraftAmount;

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-primary-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="card mb-8 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Jury Dashboard</h1>
              <p className="text-primary-100 text-lg mb-1">{participant.name}</p>
              <p className="text-primary-200 text-sm">Innovis VC Arena - An evening to invest in the future</p>
            </div>
            <div className="mt-4 md:mt-0 text-right">
              <div className="text-3xl font-bold mb-1">
                {availableBudget.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
              </div>
              <div className="text-primary-200 text-sm">Available Budget</div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            {error}
          </div>
        )}

        {/* Startups Grid */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Startups</h2>
          {startups.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-600 text-lg">No startups available. Please wait until startups are created.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {startups.map((startup) => (
                <StartupCard
                  key={startup.id}
                  startup={startup}
                  onInvest={() => handleInvest(startup)}
                  canInvest={availableBudget > 0}
                />
              ))}
            </div>
          )}
        </div>

        {/* Draft Investments Preview */}
        {draftInvestments.length > 0 && (
          <div className="card mb-8 bg-yellow-50 border-2 border-yellow-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Draft Investments (Pending Submission)</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-yellow-300">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Startup</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {draftInvestments.map((draft, index) => (
                    <tr key={draft.startupId} className="border-b border-yellow-200 hover:bg-yellow-100">
                      <td className="py-3 px-4 font-medium text-gray-900">{draft.startupName}</td>
                      <td className="py-3 px-4 text-primary-600 font-semibold">
                        {draft.amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => setDraftInvestments(prev => prev.filter((_, i) => i !== index))}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Investments History */}
        {investments.length > 0 && (
          <div className="card mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">My Submitted Investments</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Startup</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {investments.map((investment) => (
                    <tr key={investment.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{investment.startup_name}</td>
                      <td className="py-3 px-4 text-primary-600 font-semibold">
                        {parseFloat(investment.amount).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {new Date(investment.created_at).toLocaleString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Submit Button */}
        {draftInvestments.length > 0 && (
          <div className="card bg-green-50 border-2 border-green-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Ready to Submit?</h3>
                <p className="text-gray-600">
                  {draftInvestments.length} {draftInvestments.length === 1 ? 'investment' : 'investments'} pending submission
                </p>
              </div>
              <button
                onClick={handleSubmitAll}
                disabled={loading}
                className="px-8 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-xl font-semibold transition-colors text-lg"
              >
                {loading ? 'Submitting...' : 'Submit All Investments'}
              </button>
            </div>
          </div>
        )}

        {/* Investment Modal */}
        {selectedStartup && (() => {
          // Calculate available budget considering other draft investments (excluding current one)
          const currentDraft = draftInvestments.find(draft => draft.startupId === selectedStartup.id);
          const otherDraftsTotal = draftInvestments
            .filter(draft => draft.startupId !== selectedStartup.id)
            .reduce((sum, draft) => sum + draft.amount, 0);
          const availableBudgetForModal = remainingBudget - otherDraftsTotal;
          
          return (
            <InvestmentModal
              startup={selectedStartup}
              maxAmount={availableBudgetForModal}
              onClose={() => setSelectedStartup(null)}
              onInvest={handleInvestmentComplete}
              draftInvestment={currentDraft}
            />
          );
        })()}
      </div>
    </div>
  );
}

export default JuryDashboard;
